import React from "react";
import { Skeleton } from "antd";
import { VictoryPie } from "victory";
import "./InsightsRatioCard.less";
import type { InsightsRatioCardProps } from "./types";

export type { InsightsRatioCardProps } from "./types";

/** Same as InsightsDonutCard's default so rings and donuts in a row match. */
const DEFAULT_SIZE = 140;
const DEFAULT_COLOR = "#14803C";
const DEFAULT_TRACK_COLOR = "#E5E7EB";
/** Same outer/inner ratio as InsightsDonutCard, so rings across the row match. */
const INNER_RADIUS_RATIO = 33.68 / 48;

/**
 * Generic card for "N of M": a headline figure, an optional progress ring, and
 * a caption. Renders purely from `value`/`total`, so it has no knowledge of
 * what it is counting and works for any ratio.
 *
 * A ring rather than a two-slice pie — the reader is judging one quantity
 * against its maximum, not comparing two categories.
 */
export const InsightsRatioCard: React.FC<InsightsRatioCardProps> = ({
  id,
  title,
  value,
  total,
  denominator,
  caption,
  variant = "ring",
  color = DEFAULT_COLOR,
  trackColor = DEFAULT_TRACK_COLOR,
  size = DEFAULT_SIZE,
  loading = false,
  error = false,
  errorMessage,
  onRetry,
  retryLabel,
  className,
}) => {
  const rootClassName = ["insights-ratio-card", className]
    .filter(Boolean)
    .join(" ");
  const testId = `insights-ratio-card-${id}`;

  if (error) {
    return (
      <div className={rootClassName} data-testid={testId}>
        <span className="insights-ratio-card__title">{title}</span>
        <div
          className="insights-ratio-card__error"
          data-testid={`${testId}-error`}
        >
          <span className="insights-ratio-card__error-text">
            {errorMessage}
          </span>
          {onRetry && (
            <button
              type="button"
              className="insights-ratio-card__retry"
              onClick={onRetry}
            >
              {retryLabel}
            </button>
          )}
        </div>
      </div>
    );
  }

  const percent = total > 0 ? Math.round((value / total) * 100) : 0;

  const figureBlock = (
    <div className="insights-ratio-card__figure-block">
      <p className="insights-ratio-card__line">
        <span className="insights-ratio-card__figure" style={{ color }}>
          {value.toLocaleString()}
        </span>
        {denominator && (
          <span className="insights-ratio-card__denominator">
            {denominator}
          </span>
        )}
      </p>
      {caption && <p className="insights-ratio-card__caption">{caption}</p>}
    </div>
  );

  return (
    <div className={rootClassName} data-testid={testId}>
      <span className="insights-ratio-card__title">{title}</span>

      <div className="insights-ratio-card__body">
        {variant === "ring" &&
          (loading ? (
            <div data-testid={`${testId}-chart-skeleton`}>
              <Skeleton.Avatar active shape="circle" size={size} />
            </div>
          ) : (
            <div
              className="insights-ratio-card__chart"
              style={{ width: size, height: size }}
              role="img"
              aria-label={`${percent}%`}
            >
              <VictoryPie
                data={[
                  { x: "complete", y: percent },
                  { x: "remaining", y: Math.max(0, 100 - percent) },
                ]}
                innerRadius={(size / 2) * INNER_RADIUS_RATIO}
                padAngle={0}
                labels={() => null}
                colorScale={[color, trackColor]}
                width={size}
                height={size}
                padding={0}
              />
              <span
                className="insights-ratio-card__percent"
                style={{ color }}
                aria-hidden
              >
                {percent}%
              </span>
            </div>
          ))}

        {loading ? (
          <div
            className="insights-ratio-card__figure-block"
            data-testid={`${testId}-figure-skeleton`}
          >
            <Skeleton active title={false} paragraph={{ rows: 2 }} />
          </div>
        ) : (
          figureBlock
        )}
      </div>
    </div>
  );
};

export default InsightsRatioCard;
